
import { useMemo } from 'react';
import moment from 'moment';
import { Grid, TextField, Autocomplete, Tooltip, Typography, Box, InputAdornment, Checkbox } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { fieldDateFormat } from '../../../constants';
import { Info as InfoIcon } from '@mui/icons-material';
import { PermissionWrapper } from '../../../wrappers';
import { numberWithCommas, roundAmount, getTextFieldRows, getLlpClaimEventAmount } from '../../../utils';
import LessMoreTextFieldComp from '../../../shared_elements/components/LessMoreTextFieldComp';
import { hasLlpEvent, hasNonLlpEvent } from '../utils';

const PayoutCard = ({ claim, shopVisit, getResponseBack }) => {
    const isLlpEvent = hasLlpEvent(shopVisit);
    const isPrsv = shopVisit?.asset_type === 2 ? hasNonLlpEvent(shopVisit) : shopVisit?.prsv;
    const llpClaimAmount = shopVisit?.asset_type === 2 && getLlpClaimEventAmount(claim?.claim_events, 'claim_amount');
    const llpAmountPaid = shopVisit?.asset_type === 2 && getLlpClaimEventAmount(claim?.claim_events, 'amount_paid');

    const nonLlpModules = useMemo(() => {
        if (!Array.isArray(claim?.claim_events)) return [];
        const modules = [];
        claim.claim_events.forEach((event) => {
            if (event?.maintenance_program?.is_llp_event === false) {
                (event?.modules || []).forEach((mod) => {
                    modules.push({ ...mod, shop_visit_event_id: event?.shop_visit_event_id });
                });
            }
        });
        return modules;
    }, [claim?.claim_events]);

    const nonLlpEventsWithoutModules = useMemo(() => {
        if (!Array.isArray(claim?.claim_events)) return [];
        return claim.claim_events.filter((event) => {
            const isNonLlpWithoutModules = event?.maintenance_program?.is_llp_event === false && (!event?.modules || event.modules.length === 0);
            const hasRealData = Boolean(event?.maintenance_program?.name) || (event?.claim_amount !== '' && event?.claim_amount !== null && event?.claim_amount !== undefined) || (event?.amount_paid !== '' && event?.amount_paid !== null && event?.amount_paid !== undefined);
            return isNonLlpWithoutModules && hasRealData;
        });
    }, [claim?.claim_events]);

    const PayoutAndBalanceFields = () => {
        return (
            <>
                {shopVisit?.asset_type !== 2 && <Grid size={{ md: 4, xs: 12 }}>  <TextField
                    id="payout_amount"
                    value={(() => { const v = claim.amount_paid ? claim.amount_paid : claim?.claim_amount; return v !== '' && v !== null && v !== undefined ? numberWithCommas(roundAmount(v)) : v; })()}
                    label="Disbursement Amount"
                    variant="outlined"
                    size="small"
                    margin="normal"
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) }
                    }}
                    fullWidth
                    disabled={true}
                    sx={{ background: "#e9e9e9 !important" }}
                />  </Grid>}
                {shopVisit?.asset_type === 2 && isLlpEvent && <Grid size={{ md: 4, xs: 12 }}>  <TextField
                    id="llp_payout_amount"
                    value={(() => { const v = llpAmountPaid ? llpAmountPaid : llpClaimAmount; return v !== '' && v !== null && v !== undefined ? numberWithCommas(roundAmount(v)) : v; })()}
                    label="LLP Disbursement Amount"
                    variant="outlined"
                    size="small"
                    margin="normal"
                    slotProps={{
                        inputLabel: { shrink: true },
                        input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) }
                    }}
                    fullWidth
                    disabled={true}
                    sx={{ background: "#e9e9e9 !important" }}
                /> </Grid>}
                {shopVisit?.asset_type === 2 && nonLlpModules?.length > 0 ? (
                    <Grid size={{ md: 12, xs: 12 }} >
                        <Typography className='section-header-text'><u>Engine PRSV Disbursement Cost Split Details:</u></Typography>
                        <Box className="escalation-container">
                            <Box className="escalation-box">
                                <Box sx={{ display: "flex", flexWrap: "wrap", rowGap: 1.5, columnGap: 3 }}>
                                    {nonLlpModules.map((module) => {
                                        const fieldKey = `${module.shop_visit_event_id}_${module.shop_visit_event_module_id}`;
                                        const amount = module?.amount_paid ? module.amount_paid : module?.claim_amount;
                                        return (
                                            <TextField
                                                key={fieldKey}
                                                id={`${fieldKey}_Payout Amount`}
                                                label={
                                                    module?.is_non_llp ? (
                                                        <span style={{ verticalAlign: 'super' }}>
                                                            {module?.module_name}
                                                            <Tooltip placement='right' title={<span>Non-LLP</span>} arrow>
                                                                <InfoIcon fontSize='small' color='primary' style={{ verticalAlign: 'middle', marginLeft: '2px', fontSize: 'medium' }} />
                                                            </Tooltip>
                                                        </span>
                                                    ) : module?.module_name
                                                }
                                                margin="normal"
                                                slotProps={{ inputLabel: { shrink: true },  input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) } }}
                                                value={amount !== '' && amount !== null && amount !== undefined ? numberWithCommas(roundAmount(amount)) : amount}
                                                size="small"
                                                disabled={true}
                                                sx={{ width: 150, flexShrink: 0, background: "#e9e9e9 !important" }}
                                            />
                                        )
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ) : null}
                {shopVisit?.asset_type === 2 && nonLlpEventsWithoutModules?.length > 0
                    ? nonLlpEventsWithoutModules.map((event) => {
                        const amount = event?.amount_paid ? event.amount_paid : event?.claim_amount;
                        return (
                            <Grid size={{ md: 4, xs: 12 }} key={event.shop_visit_event_id}>  <TextField
                                id={`event_${event.shop_visit_event_id}_payout_amount`}
                                value={amount !== '' && amount !== null && amount !== undefined ? numberWithCommas(roundAmount(amount)) : amount}
                                label={event?.maintenance_program?.name || "PRSV Disbursement Amount"}
                                variant="outlined"
                                size="small"
                                margin="normal"
                                slotProps={{
                                    inputLabel: { shrink: true },
                                    input: { startAdornment: (<InputAdornment position="start">$</InputAdornment>) }
                                }}
                                fullWidth
                                disabled={true}
                                sx={{ background: "#e9e9e9 !important" }}
                            /> </Grid>
                        )
                    })
                    : null}
            </>
        )
    }
    return (
        <> <Grid container spacing={2} sx={{ background: "#F3EFE4", display: "flex", padding: 2 }} mt={1}>
            {claim?.payout_status?.value !== 6 && <Grid sx={{ display: "flex" }} size={{ xs: 12, md: 12 }}>
                <Grid size={{ xs: 6, md: 12 }} >
                    <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "shop_visit", operationType: ['U'] }}>
                        <Box sx={{display:"flex",alignItems:"center",gap:1}}>
                            {claim?.payout_status?.value ===2 ?  <Typography sx={{ color: '#1B5E20' }} fontSize={12}>Claim Disbursement Amount is Verified</Typography> : <Typography sx={{ color: '#B26A00' }} fontSize={12}>Claim Disbursement is Pending</Typography>}
                        </Box>
                    </PermissionWrapper>
                </Grid>
            </Grid>}

            <Grid container size={{ xs: 12, md: 12 }} >
                <PayoutAndBalanceFields />
                <Grid size={{ md: 4, xs: 12 }}>
                    <Autocomplete
                        disablePortal
                        disabled={true}
                        getOptionLabel={(option) => option.label || ""}
                        value={claim?.payout_status || null}
                        renderInput={(params) => (<TextField {...params} label="Disbursement Status" margin='normal' size='small' slotProps={{ inputLabel: { shrink: true } }} disabled={true} />)}
                    />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <TextField
                        id="reference_number"
                        value={claim?.reference_number}
                        label="Transaction Number"
                        variant="outlined"
                        size="small"
                        margin="normal"
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                        disabled={true}
                    />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                        required
                            label="Disbursement Date *"
                            value={claim?.payout_date ? moment(claim.payout_date) : null}
                            slotProps={{
                                textField: {
                                    InputLabelProps: { shrink: true },
                                    size: "small",
                                    margin: "normal",
                                },
                            }}
                            sx={{ width: "100%" }}
                            format={fieldDateFormat}
                            disabled={true}
                        />
                    </LocalizationProvider>
                </Grid>
                {claim?.payout_status?.value === 6 ? (
                    <Grid size={{ md: 4, xs: 12 }}>
                        <Box className="check-item-div" gap={1} sx={{ cursor: "not-allowed" }}>
                            <Checkbox sx={{ p: 0 }}
                                checked={claim?.is_claim_carry_forward === undefined || claim?.is_claim_carry_forward === null ? true : claim.is_claim_carry_forward}
                                disabled={true}
                                disableRipple
                            />
                            <span className="check-item-span">Carry Forward Fund Balance</span>
                        </Box>
                    </Grid>
                ) : null}
                <Grid size={{ md: 12, xs: 12 }}>
                    <TextField
                        id="transaction_description"
                        label="Transaction Details"
                        variant="outlined"
                        size='small'
                        margin='normal'
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                        multiline
                        rows={getTextFieldRows(claim?.transaction_description , 3, 65)}
                        value={claim?.transaction_description || ''}
                        disabled={true}
                    />
                </Grid>
            </Grid></Grid>
        </>

    )
}

export default PayoutCard